import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Modal, PageHead, WaButton, inputCls, itDate, saveDoc, useList } from "@/components/kit";
import { BrochureButton } from "@/components/BrochureButton";
import { api, errMsg, fmtMoney } from "@/lib/api";

const LABELS = { nuovo: "Nuova", contattato: "Contattato", sopralluogo: "Sopralluogo", degustazione: "Degustazione", preventivo: "Preventivo", contratto: "Contratto", perso: "Perso" };

function LeadForm({ lead, opts, onDone }) {
  const [f, setF] = useState({ nome: "", tel: "", email: "", fonte: "", stage: "nuovo", evento_tipo: "", evento_data: "", sede: "", ospiti: "", budget: "", prossimo_contatto: "", note: "", motivo_perso: "", ...lead });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const sel = (k, list, tid) => <select className={inputCls} value={f[k]} onChange={set(k)} data-testid={tid}><option value="">—</option>{list.map((o) => <option key={o} value={o}>{LABELS[o] || o}</option>)}</select>;
  const save = async () => {
    const { id, created_at, updated_at, created_by, ...body } = f;
    if (await saveDoc("/leads", id, body)) onDone();
  };
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nome e cognome *"><input className={inputCls} value={f.nome} onChange={set("nome")} data-testid="lead-name" /></Field>
        <Field label="Telefono"><input className={inputCls} value={f.tel} onChange={set("tel")} data-testid="lead-phone" /></Field>
        <Field label="E-mail"><input className={inputCls} value={f.email} onChange={set("email")} data-testid="lead-email" /></Field>
        <Field label="Da dove arriva">{sel("fonte", opts.sources, "lead-source")}</Field>
        <Field label="Fase">{sel("stage", opts.stages, "lead-stage")}</Field>
        <Field label="Tipo evento">{sel("evento_tipo", opts.event_types, "lead-type")}</Field>
        <Field label="Data evento"><input type="date" className={inputCls} value={f.evento_data} onChange={set("evento_data")} data-testid="lead-date" /></Field>
        <Field label="Sede">{sel("sede", opts.sedi, "lead-sede")}</Field>
        <Field label="Ospiti previsti"><input type="number" className={inputCls} value={f.ospiti} onChange={set("ospiti")} data-testid="lead-guests" /></Field>
        <Field label="Budget indicativo (€)"><input type="number" className={inputCls} value={f.budget} onChange={set("budget")} data-testid="lead-budget" /></Field>
        <Field label="Ricontattare il"><input type="date" className={inputCls} value={f.prossimo_contatto} onChange={set("prossimo_contatto")} data-testid="lead-next" /></Field>
        {f.stage === "perso" && <Field label="Motivo"><input className={inputCls} value={f.motivo_perso} onChange={set("motivo_perso")} data-testid="lead-lost-reason" /></Field>}
        <Field label="Note" wide><textarea rows={3} className={inputCls} value={f.note} onChange={set("note")} data-testid="lead-note" /></Field>
      </div>
      <Button className="rounded-full" onClick={save} disabled={!f.nome.trim()} data-testid="lead-save">Salva richiesta</Button>
    </div>
  );
}

function LeadCard({ l, open, toQuote, move, stages, replied }) {
  return (
    <div className="rounded-lg border border-[#D4E6EC] bg-white p-3 space-y-2 text-sm hover:shadow-sm transition-shadow" data-testid={`lead-card-${l.id}`}>
      <button className="text-left w-full" onClick={() => open(l)} data-testid={`lead-open-${l.id}`}>
        <div className="font-medium">{l.nome}</div>
        <div className="text-xs text-[#7B929C]">{[l.evento_tipo, itDate(l.evento_data), l.ospiti && `${l.ospiti} ospiti`, l.budget && fmtMoney(l.budget)].filter(Boolean).join(" • ")}</div>
        {l.fonte && <div className="text-[10px] uppercase tracking-wider text-[#34B3B0] mt-1">{l.fonte}{l.online && <span className="ml-1 rounded-full bg-[#34B3B0] text-white px-1.5 py-px normal-case tracking-normal" data-testid={`lead-online-${l.id}`}>online</span>}{l.referral_code && <span className="ml-1 normal-case tracking-normal text-[#B45309]">🎁 {l.referral_code}</span>}</div>}
        {l.online && !l.risposto_at && <div className="text-[11px] text-[#B45309] font-medium mt-1" data-testid={`lead-unanswered-${l.id}`}>⏱ Da rispondere • arrivata {new Date(l.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>}
      </button>
      <div className="flex flex-wrap gap-1.5 items-center">
        <select className="text-xs rounded-full border border-[#D4E6EC] px-2 py-1" value={l.stage} onChange={(e) => move(l, e.target.value)} data-testid={`lead-move-${l.id}`}>{stages.map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}</select>
        <WaButton phone={l.tel} testId={`lead-wa-${l.id}`} onSent={() => replied(l)} text={`🌟 Gentile *${l.nome}*,\n\n🙏 grazie per averci contattato${l.evento_tipo ? ` per il suo *${l.evento_tipo.toLowerCase()}*` : " per il suo evento"}${l.evento_data ? ` del *${itDate(l.evento_data)}*` : ""}! ✨ Saremo felici di aiutarla a organizzarlo al meglio.\n📅 Quando le farebbe piacere passare per un sopralluogo o una degustazione?\n\nUn cordiale saluto 🌊`} />
        <Button size="sm" variant="ghost" className="rounded-full" onClick={() => toQuote(l)} data-testid={`lead-quote-${l.id}`}><FileText size={13} className="mr-1" /> {l.quote_id ? "Preventivo" : "Crea preventivo"}</Button>
      </div>
    </div>
  );
}

export default function Leads() {
  const nav = useNavigate();
  const [rows, reload] = useList("/leads");
  const [opts, setOpts] = useState(null);
  const [edit, setEdit] = useState(null);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("new")) {
      setEdit({});
      setParams({});
    }
  }, [params, setParams]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/crm/options").then((r) => setOpts(r.data));
  }, []);
  const list = useMemo(() => (rows || []).filter((l) => !q || `${l.nome} ${l.tel} ${l.evento_tipo}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const move = async (l, stage) => {
    try {
      await api.put(`/leads/${l.id}`, { stage });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toQuote = async (l) => {
    try {
      const r = await api.post(`/leads/${l.id}/quote`);
      nav(`/preventivi/${r.data.quote_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    await api.delete(`/leads/${edit.id}`);
    setEdit(null);
    reload();
  };
  if (!opts) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Contatti in arrivo e trattative" title="Richieste">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className={`${inputCls} rounded-full w-48`} data-testid="leads-search" />
        <Button className="rounded-full" onClick={() => setEdit({})} data-testid="new-lead-button"><Plus size={15} className="mr-2" /> Nuova richiesta</Button>
      </PageHead>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="leads-board">
        {opts.stages.map((s) => {
          const col = list.filter((l) => (l.stage || "nuovo") === s);
          return (
            <div key={s} className="min-w-[260px] w-[260px] shrink-0 rounded-xl bg-[#EAF5F7]/60 p-3 space-y-2" data-testid={`lead-col-${s}`}>
              <div className="flex justify-between text-xs uppercase tracking-wider text-[#47606B] px-1"><span>{LABELS[s]}</span><span>{col.length}</span></div>
              {col.map((l) => <LeadCard key={l.id} l={l} open={setEdit} toQuote={toQuote} move={move} stages={opts.stages} replied={(x) => api.post(`/leads/${x.id}/replied`).then(reload)} />)}
            </div>
          );
        })}
      </div>
      <Modal open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? edit.nome : "Nuova richiesta"} testId="lead-dialog">
        {edit && <LeadForm lead={edit} opts={opts} onDone={() => { setEdit(null); reload(); }} />}
        {edit?.id && <div className="flex flex-wrap gap-2 items-center"><BrochureButton source="lead" id={edit.id} phone={edit.tel} nome={edit.nome} /><Button variant="ghost" className="rounded-full text-[#991B1B]" onClick={remove} data-testid="lead-delete"><Trash2 size={14} className="mr-1" /> Elimina</Button></div>}
      </Modal>
    </div>
  );
}
